import { createGetAsyncSlice } from '@sonar-software/xuder';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { updateSonarObjectReferences } from '../sonar-objects/_idMap/actions';

import {
	DataDictionaryField,
	DataDictionaryObject,
	ResponsibilityType,
	UpdateSonarObjectReferenceRequest
} from '@types';
import { getDataDictionary, GetDataDictionaryByTypeRequest } from '@api';

function buildBaseUpdate(entry: DataDictionaryField | DataDictionaryObject): UpdateSonarObjectReferenceRequest {
	return {
		context: {
			definition: entry.definition,
			notes: entry.notes
		},
		ingestedBy: entry.ingestedBy,
		populatedBy: entry.populatedBy,
		salesforce: {
			description: entry.description,
			managedPackage: entry.managedPackage
		},
		sonarObjectId: entry.sonarObjectId,
		stakeholders: entry.stakeholderIds,
		users: entry.ownerIds.map((ownerId) => ({
			responsibilityType: ResponsibilityType.Owner,
			userId: ownerId
		}))
	};
}

export function buildUpdateForField(field: DataDictionaryField): UpdateSonarObjectReferenceRequest {
	const base = buildBaseUpdate(field);

	return {
		...base,
		aiGeneratedTracking: field.aiGeneratedTracking,
		salesforce: {
			...base.salesforce,
			complianceCategory: field.complianceCategory || undefined,
			dataSensitivityLevelId: field.dataSensitivityLevelId,
			fieldUsageId: field.fieldUsageId,
			helpText: field.helpText,
			isCustom: field.custom
		},
		sonarObjectType: SonarObjectType.SalesforceField,
		tags: field.tagIds ? field.tagIds : undefined
	};
}

export function buildUpdateForObject(
	object: DataDictionaryObject,
	objectType: SonarObjectType
): UpdateSonarObjectReferenceRequest {
	const base = buildBaseUpdate(object);

	return {
		...base,
		salesforce: {
			...base.salesforce,
			isCustom: object.isCustom
		},
		sonarObjectType: objectType
	};
}

export const getCoreDataDictionarySlice = createGetAsyncSlice({
	httpSagaOptions: {
		successCombinationActionCreators: [
			(request, response) => {
				const objectType = (request as GetDataDictionaryByTypeRequest).sonarObjectType;

				const context: Array<UpdateSonarObjectReferenceRequest> = response.results.map((result) =>
					(objectType && objectType.toLowerCase() !== SonarObjectType.SalesforceField.toLowerCase() ?
						buildUpdateForObject(result as DataDictionaryObject, objectType) :
						buildUpdateForField(result as DataDictionaryField)));

				return updateSonarObjectReferences(context);
			}
		]
	},
	name: 'CoreDataDictionary',
	receive: true,
	request: true,
	requestHandler: getDataDictionary,
	retainDataOnRequest: true
});
